import axios from 'axios';
import {
  SESSION_EXPIRED_REASON,
  getAuthHeaders,
  getLoginPath,
  getStoredAccessToken,
  getStoredCompanyId,
  hasUsableStoredValue,
  handleExpiredSession,
} from './authSession';

let interceptorsInstalled = false;

const PUBLIC_AUTH_PATHS = [
  '/api/token/',
  '/api/token/refresh/',
  '/api/superadmin/mfa/challenge/',
  '/api/superadmin/mfa/enroll/confirm/',
  '/api/superadmin/mfa/verify/',
  '/api/superadmin/mfa/recovery/',
  '/api/verify-company-password/',
  '/api/plan-enquiry/',
];

const OPTIONAL_AUTH_PATHS = [
  '/api/browser-security/errors/',
  '/api/browser-security/alerts/',
];

const getPathname = (url = '') => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url || '');
  }
};

const isPublicAuthRequest = (url) => {
  const pathname = getPathname(url);
  return PUBLIC_AUTH_PATHS.some((path) => pathname === path || pathname.endsWith(path));
};

const isOptionalAuthRequest = (url) => {
  const pathname = getPathname(url);
  return OPTIONAL_AUTH_PATHS.some((path) => pathname === path || pathname.endsWith(path));
};

const getHeaderValue = (headers, headerName) => {
  if (!headers) return '';

  if (typeof headers.get === 'function') {
    return headers.get(headerName) || headers.get(headerName.toLowerCase()) || '';
  }

  const headerEntry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === headerName.toLowerCase()
  );
  return headerEntry ? headerEntry[1] : '';
};

const clearHeaderValue = (headers, headerName) => {
  if (!headers) return;

  if (typeof headers.delete === 'function') {
    headers.delete(headerName);
    headers.delete(headerName.toLowerCase());
    return;
  }

  Object.keys(headers).forEach((key) => {
    if (key.toLowerCase() === headerName.toLowerCase()) {
      delete headers[key];
    }
  });
};

const isUsableBearerHeader = (value) => {
  const header = String(value || '').trim();
  if (!/^Bearer\s+\S+$/i.test(header)) return false;
  const token = header.replace(/^Bearer\s+/i, '');
  return hasUsableStoredValue(token);
};

export const installAxiosAuthInterceptors = () => {
  if (interceptorsInstalled) return;

  axios.interceptors.request.use((config) => {
    const requestUrl = config.url || '';
    const existingAuthHeader = getHeaderValue(config.headers, 'Authorization');
    const token = getStoredAccessToken();
    const hasManualAuthHeader = isUsableBearerHeader(existingAuthHeader);

    config.__putpensHadAccessToken = Boolean(token) || hasManualAuthHeader;
    config.__putpensSkipAuthRedirect = Boolean(config.__skipAuthRedirect);

    if (isPublicAuthRequest(requestUrl)) {
      return config;
    }

    if (hasManualAuthHeader) {
      return config;
    }

    if (!token) {
      if (existingAuthHeader && !isUsableBearerHeader(existingAuthHeader)) {
        clearHeaderValue(config.headers, 'Authorization');
      }
      return config;
    }

    config.headers = config.headers || {};
    Object.entries(getAuthHeaders(token)).forEach(([key, value]) => {
      config.headers[key] = value;
    });
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const requestUrl = error?.config?.url || '';
      const hadAccessToken = Boolean(error?.config?.__putpensHadAccessToken)
        || isUsableBearerHeader(getHeaderValue(error?.config?.headers, 'Authorization'));
      const shouldSkipRedirect = Boolean(error?.config?.__putpensSkipAuthRedirect)
        || isPublicAuthRequest(requestUrl)
        || isOptionalAuthRequest(requestUrl);

      if (status === 401 && hadAccessToken && !shouldSkipRedirect) {
        const companyId = getStoredCompanyId() || '1';
        handleExpiredSession({
          companyId,
        });

        if (typeof window !== 'undefined') {
          window.location.replace(getLoginPath({
            companyId,
            reason: SESSION_EXPIRED_REASON,
          }));
        }
      }

      return Promise.reject(error);
    }
  );

  interceptorsInstalled = true;
};
